// Smart Sash Admin Panel - Group Store
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  GroupDTO,
  CreateGroupRequest,
  UpdateGroupRequest,
  PaginationMetadata,
} from '@/models';
import { groupService } from '@/services/api/groupService';
import { getErrorMessage } from '@/utils/getErrorMessage';

interface GroupState {
  groups: GroupDTO[];
  selectedGroup: GroupDTO | null;
  pagination: PaginationMetadata | null;
  isLoading: boolean;
  error: string | null;
  fetchGroups: (page?: number, pageSize?: number) => Promise<void>;
  fetchGroupById: (id: string) => Promise<void>;
  createGroup: (data: CreateGroupRequest) => Promise<GroupDTO>;
  updateGroup: (id: string, data: UpdateGroupRequest) => Promise<void>;
  deleteGroup: (id: string) => Promise<void>;
  setSelectedGroup: (group: GroupDTO | null) => void;
  clearError: () => void;
}

export const useGroupStore = create<GroupState>()(
  devtools(
    (set) => ({
      groups: [],
      selectedGroup: null,
      pagination: null,
      isLoading: false,
      error: null,
      fetchGroups: async (page = 1, pageSize = 10) => {
        set({ isLoading: true, error: null });
        try {
          const response = await groupService.getAll({ page, pageSize });
          set({
            groups: response.data,
            pagination: response.pagination,
            isLoading: false,
          });
        } catch (error: unknown) {
          set({
            error: getErrorMessage(error, 'Failed to fetch groups'),
            isLoading: false,
          });
          throw error;
        }
      },
      fetchGroupById: async (id: string) => {
        set({ isLoading: true, error: null });
        try {
          const group = await groupService.getById(id);
          set({ selectedGroup: group, isLoading: false });
        } catch (error: unknown) {
          set({
            error: getErrorMessage(error, 'Failed to fetch group'),
            isLoading: false,
          });
          throw error;
        }
      },
      createGroup: async (data: CreateGroupRequest) => {
        set({ isLoading: true, error: null });
        try {
          const newGroup = await groupService.create(data);
          set((state) => ({
            groups: [...state.groups, newGroup],
            isLoading: false,
          }));
          return newGroup;
        } catch (error: unknown) {
          set({
            error: getErrorMessage(error, 'Failed to create group'),
            isLoading: false,
          });
          throw error;
        }
      },
      updateGroup: async (id: string, data: UpdateGroupRequest) => {
        set({ isLoading: true, error: null });
        try {
          const updatedGroup = await groupService.update(id, data);
          set((state) => ({
            groups: state.groups.map((g) => (g.id === id ? updatedGroup : g)),
            selectedGroup:
              state.selectedGroup?.id === id ? updatedGroup : state.selectedGroup,
            isLoading: false,
          }));
        } catch (error: unknown) {
          set({
            error: getErrorMessage(error, 'Failed to update group'),
            isLoading: false,
          });
          throw error;
        }
      },
      deleteGroup: async (id: string) => {
        set({ isLoading: true, error: null });
        try {
          await groupService.delete(id);
          set((state) => ({
            groups: state.groups.filter((g) => g.id !== id),
            selectedGroup:
              state.selectedGroup?.id === id ? null : state.selectedGroup,
            isLoading: false,
          }));
        } catch (error: unknown) {
          set({
            error: getErrorMessage(error, 'Failed to delete group'),
            isLoading: false,
          });
          throw error;
        }
      },
      setSelectedGroup: (group: GroupDTO | null) => set({ selectedGroup: group }),
      clearError: () => set({ error: null }),
    }),
    { name: 'GroupStore' }
  )
);
